import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';
/* ================= WRAP ================= */

const Wrap = styled.div`
  width: min(100%, 1800px);
  margin: clamp(12px, 3vw, 28px) auto;
  padding: 0px;
  display: grid;
  gap: 26px;
`;

/* ================= TOP ================= */

const Top = styled.section`
  background: linear-gradient(180deg, var(--topFrom), var(--topTo));
  border-radius: 36px;
  padding: clamp(20px, 3vw, 36px);
  border: 1px solid var(--topBorder);
`;

const Title = styled.h1`
  margin: 0;
  color: #fff;
  font-size: clamp(28px, 3.6vw, 44px);
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: rgba(255, 255, 255, 0.75);
  max-width: 720px;
  line-height: 1.55;
  overflow-wrap: anywhere;
`;

/* ================= CATEGORIES ================= */

const Categories = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Category = styled.button`
  border: none;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 13px;

  background: ${({ active }) => (active ? 'var(--primaryBtnBg)' : 'rgba(255,255,255,0.08)')};
  color: ${({ active }) => (active ? 'var(--primaryBtnText)' : '#fff')};

  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ================= FEATURED ================= */

const Featured = styled.section`
  background: rgba(255, 255, 255, 0.96);
  border-radius: 32px;
  padding: clamp(18px, 2.8vw, 30px);
  display: grid;
  gap: 14px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
`;

const FeaturedBadge = styled.span`
  width: fit-content;
  background: rgba(255, 138, 31, 0.18);
  border: 1px solid rgba(255, 138, 31, 0.35);
  color: #111;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
`;

const FeaturedTitle = styled.h2`
  margin: 0;
  font-size: clamp(22px, 2.6vw, 34px);
  font-weight: 900;
  color: #0b1b2b;
`;

const Meta = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
`;

const PrimaryBtn = styled.button`
  width: fit-content;
  border: none;
  border-radius: 14px;
  padding: 12px 18px;
  font-weight: 900;
  background: var(--primaryBtnBg);
  color: var(--primaryBtnText);
  cursor: pointer;
`;

/* ================= GRID ================= */

const Grid = styled.section`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  background: rgba(255, 255, 255, 0.96);
  border-radius: 24px;
  padding: 16px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.14);

  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  min-width: 0;
`;

const Avatar = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 999px;
  flex: 0 0 auto;

  display: grid;
  place-items: center;
  font-size: 22px;
  font-weight: 900;

  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const CardBadge = styled.span`
  width: fit-content;
  background: rgba(0, 0, 0, 0.06);
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 900;
  color: #0b1b2b;
  overflow-wrap: anywhere;
`;

const CardText = styled.div`
  font-size: 14px;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.7);

  overflow-wrap: anywhere;

  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  a {
    color: #dc0303;
    text-decoration: underline;
    font-weight: 800;
  }
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
`;

const CardBtn = styled.button`
  margin-top: auto;
  width: fit-content;
  border: none;
  border-radius: 14px;
  padding: 10px 14px;
  font-weight: 900;
  background: var(--cardBtnBg);
  color: var(--cardBtnText);
  cursor: pointer;
`;

/* ================= MODAL ================= */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  display: grid;
  place-items: center;
  padding: 18px;
  z-index: 9999;
`;

const Modal = styled.div`
  width: min(920px, 100%);
  max-height: 85vh;
  overflow: auto;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 22px;
  padding: 18px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-weight: 900;
  color: #0b1b2b;
`;

const CloseBtn = styled.button`
  border: none;
  cursor: pointer;
  border-radius: 12px;
  padding: 10px 12px;
  font-weight: 900;
  background: rgba(0, 0, 0, 0.08);
`;

const ModalBody = styled.div`
  margin-top: 14px;
  font-size: 15px;
  line-height: 1.65;
  color: rgba(0, 0, 0, 0.75);

  a {
    color: #dc0303;
    text-decoration: underline;
    font-weight: 800;
  }
`;

/* ✅ FIX "/api/api/..." */
function safeApiPath(path) {
  const p = String(path || '');
  if (!p) return p;

  const normalized = p.replace(/\/{2,}/g, '/');

  if (normalized.startsWith('/api/')) return normalized.replace(/^\/api/, '');
  if (normalized.startsWith('api/')) return normalized.replace(/^api/, '');

  if (normalized === '/api') return '/';
  if (normalized === 'api') return '/';

  return normalized;
}

/* ================= BLOG ================= */

export default function Blog() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('it') ? 'it' : 'en';

  const pick = (obj, key, fallback = '') => {
    if (!obj) return fallback;
    return (lang === 'it' ? obj[`${key}_it`] : obj[`${key}_en`]) || obj[`${key}_en`] || fallback;
  };

  const [data, setData] = useState(null);
  const [activeSlug, setActiveSlug] = useState('all');
  const [openPost, setOpenPost] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(safeApiPath('/blog-page/'));
        setData(res.data);
      } catch (err) {
        console.error('BLOG LOAD ERROR:', err);
      }
    };

    load();
  }, []);

  useEffect(() => {
    const esc = (e) => e.key === 'Escape' && setOpenPost(null);
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);

  const filteredPosts = useMemo(() => {
    if (!data?.posts) return [];
    const all = data.posts.filter((p) => !p.is_featured);
    return activeSlug === 'all' ? all : all.filter((p) => p.category?.slug === activeSlug);
  }, [data, activeSlug]);

  if (!data) return null;

  const page = data.page;

  const styleVars = {
    '--topFrom': page.top_bg_from,
    '--topTo': page.top_bg_to,
    '--topBorder': page.top_border_color,
    '--primaryBtnBg': page.primary_btn_bg,
    '--primaryBtnText': page.primary_btn_text_color,
    '--cardBtnBg': page.card_btn_bg,
    '--cardBtnText': page.card_btn_text_color,
  };

  const getAvatar = (p) => {
    const letter = (p.category?.name || p.title)[0].toUpperCase();
    const hue = (letter.charCodeAt(0) * 7) % 360;
    return {
      letter,
      style: {
        background: `hsla(${hue},85%,88%,1)`,
        color: `hsla(${hue},60%,22%,1)`,
      },
    };
  };

  const seoTitle = pick(page, 'title', 'Blog');
  const seoDesc = pick(page, 'subtitle', 'Latest news and updates from our blog.');

  return (
    <Wrap style={styleVars}>
      <SEO title={seoTitle} description={seoDesc} path="/blog" />

      <Top>
        <Title>{pick(page, 'title', 'Blog')}</Title>
        <Subtitle>{pick(page, 'subtitle', '')}</Subtitle>

        <Categories>
          <Category active={activeSlug === 'all'} onClick={() => setActiveSlug('all')}>
            {pick(page, 'all_articles_text', 'All articles')}
          </Category>

          {data.categories.map((c) => (
            <Category
              key={c.id}
              active={activeSlug === c.slug}
              onClick={() => setActiveSlug(c.slug)}
            >
              {c.name}
            </Category>
          ))}
        </Categories>
      </Top>

      {data.featured && (
        <Featured>
          <FeaturedBadge>{pick(page, 'featured_badge_text', 'Featured')}</FeaturedBadge>
          <FeaturedTitle>{data.featured.title}</FeaturedTitle>
          <ReactMarkdown>{data.featured.excerpt}</ReactMarkdown>
          <Meta>{data.featured.meta}</Meta>
          <PrimaryBtn onClick={() => setOpenPost(data.featured)}>
            {pick(page, 'primary_btn_text', 'Read article')}
          </PrimaryBtn>
        </Featured>
      )}

      <Grid>
        {filteredPosts.map((p) => {
          const a = getAvatar(p);
          return (
            <Card key={p.id}>
              <Row>
                <Avatar style={a.style}>{a.letter}</Avatar>
                <div>
                  <CardBadge>{p.category?.name}</CardBadge>
                  <CardTitle>{p.title}</CardTitle>
                </div>
              </Row>

              <CardText>
                <ReactMarkdown>{p.excerpt}</ReactMarkdown>
              </CardText>

              <CardMeta>{p.meta}</CardMeta>
              <CardBtn onClick={() => setOpenPost(p)}>
                {pick(page, 'card_btn_text', 'Read post')}
              </CardBtn>
            </Card>
          );
        })}
      </Grid>

      {openPost && (
        <Overlay onClick={() => setOpenPost(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{openPost.title}</ModalTitle>
            <Meta>{openPost.meta}</Meta>
            <ModalBody>
              <ReactMarkdown>{openPost.body || openPost.excerpt}</ReactMarkdown>
            </ModalBody>
            <CloseBtn onClick={() => setOpenPost(null)}>
              {pick(page, 'close_btn_text', 'Close')} ✕
            </CloseBtn>
          </Modal>
        </Overlay>
      )}
    </Wrap>
  );
}
